import { useEffect } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import useFeatureFlag from "@/hooks/useFeatureFlag";
import { useFeatureFlags } from "@/context/FeatureFlagsContext";
import { Loader2 } from "lucide-react";

/**
 * Renderizza `children` solo se il feature flag è abilitato per l'utente corrente.
 * Uso:
 *   <FeatureFlag flag="report_settimanali"><Foo /></FeatureFlag>
 *
 * Prop `fallback` opzionale per mostrare qualcosa in caso di off.
 * Attende che il context flags sia `loaded` prima di decidere (evita flicker/redirect prematuro).
 */
export default function FeatureFlag({ flag, children, fallback = null }) {
  const enabled = useFeatureFlag(flag);
  const { loaded } = useFeatureFlags();
  if (!loaded) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground" data-testid="ff-loading">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  if (!enabled) return fallback;
  return children;
}

/**
 * Redirect a `to` (default "/") con toast informativo, usato come fallback quando
 * l'utente naviga a URL di feature non abilitata.
 */
export function FeatureFlagRedirect({ to = "/", message = "Feature non abilitata per il tuo utente." }) {
  useEffect(() => {
    toast.error(message);
  }, [message]);
  return <Navigate to={to} replace />;
}
